import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { Hub, ZoneLocationSearch } from '@/services/api/hubs';

interface HubPickerDropdownProps {
  hubs: Hub[];
  /**
   * Client pitstops (PROVIDER_WAREHOUSE, mapped to the Hub shape) — zones can
   * be drawn for these too (client-pitstop pickup TRD D8). Selection flows
   * through the same `onPick`/`?hub=` wiring as a Dash hub.
   */
  pitstops?: Hub[];
  /** Owning client name per pitstop id, for the section rows. */
  pitstopClientNames?: Record<number, string>;
  /** Zone tally per hub id — drives the "sudah punya zone" grouping. */
  zoneCounts?: Record<number, number>;
  /**
   * Server-side lookup. The preloaded lists are only the first page of each
   * type, so filtering them locally would quietly hide everything past it.
   */
  onSearch?: (query: string) => Promise<ZoneLocationSearch>;
  activeHubId: number | null;
  onPick: (hubId: number) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
/** Below this a search matches most of the masterdata and isn't worth a trip. */
const MIN_SEARCH_LENGTH = 2;

/**
 * The Zones-editor location picker. Lives in the slim top bar.
 *
 * The reachable masterdata runs to the hundreds of hubs and client pitstops,
 * but only a handful have zones drawn — and those are the ones ops switches
 * between all day. So the list leads with them and files the rest under a
 * second heading, with a search box for reaching anything by name or code.
 * Built on the kit DropdownMenu — Radix owns click-outside, Escape, and focus.
 */
const HubPickerDropdown: React.FC<HubPickerDropdownProps> = ({
  hubs,
  pitstops = [],
  pitstopClientNames = {},
  zoneCounts = {},
  onSearch,
  activeHubId,
  onPick,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ZoneLocationSearch | null>(null);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const total = hubs.length + pitstops.length;
  const activeHub =
    hubs.find((h) => h.id === activeHubId) ??
    pitstops.find((p) => p.id === activeHubId) ??
    null;

  const term = query.trim();
  const isSearching = term.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!onSearch || !isSearching) {
      setResults(null);
      setSearching(false);
      return;
    }
    let stale = false;
    setSearching(true);
    const timer = setTimeout(() => {
      onSearch(term)
        .then((found) => {
          if (!stale) setResults(found);
        })
        .catch(() => {
          if (!stale) setResults({ hubs: [], pitstops: [], clientNames: {} });
        })
        .finally(() => {
          if (!stale) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [onSearch, term, isSearching]);

  const { zoned, rest } = useMemo(() => {
    const sourceHubs = results ? results.hubs : hubs;
    const sourcePitstops = results ? results.pitstops : pitstops;
    const names = results ? results.clientNames : pitstopClientNames;
    const all = [
      ...sourceHubs.map((h) => ({
        hub: h,
        clientName: undefined as string | undefined,
      })),
      ...sourcePitstops.map((p) => ({
        hub: p,
        clientName: names[p.id] || undefined,
      })),
    ];

    // The active hub belongs up top even before its first zone exists —
    // otherwise picking an empty hub makes it vanish from the section it was
    // just selected in.
    const isZoned = (id: number) =>
      (zoneCounts[id] ?? 0) > 0 || id === activeHubId;

    return {
      zoned: all
        .filter((row) => isZoned(row.hub.id))
        .sort(
          (a, b) => (zoneCounts[b.hub.id] ?? 0) - (zoneCounts[a.hub.id] ?? 0)
        ),
      rest: all.filter((row) => !isZoned(row.hub.id)),
    };
  }, [
    results,
    hubs,
    pitstops,
    pitstopClientNames,
    zoneCounts,
    activeHubId,
  ]);

  const renderRow = (hub: Hub, clientName?: string) => {
    const isActive = hub.id === activeHubId;
    const count = zoneCounts[hub.id] ?? 0;
    return (
      <DropdownMenuItem
        key={hub.id}
        onSelect={() => onPick(hub.id)}
        className={isActive ? 'bg-bg-weak-50' : undefined}
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
            isActive ? 'bg-accent' : 'bg-bg-soft-200'
          }`}
        />
        <div className="min-w-0 flex-1">
          <p
            className={`truncate font-mono text-[10.5px] tabular ${
              isActive ? 'text-accent' : 'text-text-sub-600'
            }`}
          >
            {hub.code}
          </p>
          <p
            className={`truncate text-[12px] tracking-tight ${
              isActive ? 'font-semibold text-accent' : 'text-text-strong-950'
            }`}
          >
            {hub.name}
            {clientName ? (
              <span className="ml-1 text-[11px] font-normal text-text-sub-600">
                · {clientName}
              </span>
            ) : null}
          </p>
        </div>
        {count > 0 && (
          <span className="shrink-0 rounded-md bg-bg-weak-50 px-1.5 py-0.5 font-mono text-[10px] font-bold tabular text-text-sub-600">
            {count}
          </span>
        )}
      </DropdownMenuItem>
    );
  };

  const heading = (text: string) => (
    <p className="px-2 pb-1 pt-2 text-[9px] font-bold uppercase tracking-[0.18em] text-text-soft-400">
      {text}
    </p>
  );

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) {
          setQuery('');
          setResults(null);
        }
        // Radix moves focus to the first item on open; hand it to the search
        // box instead so typing filters rather than jumping between rows.
        else setTimeout(() => searchRef.current?.focus(), 0);
      }}
    >
      {/* Empty preload is not a dead end when search can still reach the
          masterdata — the search box lives INSIDE this dropdown, so locking
          the trigger would lock the only way out. */}
      <DropdownMenuTrigger asChild disabled={total === 0 && !onSearch}>
        <button
          type="button"
          className="group inline-flex items-center gap-2 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-2.5 py-1 transition-colors hover:bg-bg-weak-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-accent" />
          {activeHub ? (
            <>
              <span className="font-mono text-[11px] tabular text-text-sub-600">
                {activeHub.code}
              </span>
              <span className="text-[12px] font-semibold tracking-tight text-text-strong-950">
                {activeHub.name}
              </span>
            </>
          ) : (
            <span className="text-[12px] text-text-sub-600">
              {total === 0 ? 'No active hubs' : 'Pick a hub'}
            </span>
          )}
          <i className="ri-arrow-down-s-line text-[14px] text-text-soft-400 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[360px]">
        <div className="flex items-center justify-between px-2 pb-2">
          <p className="label-eyebrow">Switch hub</p>
          <span className="rounded-md border border-stroke-soft-200 bg-bg-weak-50 px-1.5 py-0.5 font-mono text-[10px] font-bold tabular text-text-sub-600">
            {total}
          </span>
        </div>

        <div className="px-2 pb-2">
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[13px] text-text-soft-400" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              // Radix DropdownMenu treats typing as type-ahead over its items
              // and steals the keystrokes; the search box has to keep them.
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Cari kode, nama, atau client…"
              className="h-8 w-full rounded-lg border border-stroke-soft-200 bg-bg-white-0 pl-8 pr-2.5 text-[12px] text-text-strong-950 outline-none transition-colors placeholder:text-text-soft-400 focus:border-accent"
            />
          </div>
        </div>

        <div className="max-h-[320px] overflow-y-auto">
          {searching && (
            <p className="px-2 py-6 text-center text-[12px] text-text-sub-600">
              <i className="ri-loader-4-line mr-1.5 animate-spin" />
              Mencari…
            </p>
          )}
          {!searching && zoned.length === 0 && rest.length === 0 && (
            <p className="px-2 py-6 text-center text-[12px] text-text-sub-600">
              {term.length > 0 && term.length < MIN_SEARCH_LENGTH
                ? 'Ketik minimal 2 huruf.'
                : 'Tidak ada hub yang cocok.'}
            </p>
          )}
          {!searching && zoned.length > 0 && (
            <>
              {heading('Sudah punya zone')}
              {zoned.map((row) => renderRow(row.hub, row.clientName))}
            </>
          )}
          {!searching && rest.length > 0 && (
            <>
              {heading(zoned.length > 0 ? 'Belum ada zone' : 'Hub lain')}
              {rest.map((row) => renderRow(row.hub, row.clientName))}
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HubPickerDropdown;
